import type {
  ActivateInstitutionTrialPayload,
  AddAdminInstitutionMemberPayload,
  AdminInstitutionDetail,
  AdminInstitutionMember,
  ListAdminInstitutionsPayload,
  ListAdminInstitutionsResponse,
  OfferInstitutionSeatPayload,
  UpdateAdminInstitutionPayload,
  UpdateInstitutionTrialPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type AdminInstitutionResponse = { institution: AdminInstitutionDetail };
type AdminInstitutionMutationResponse = { institution: AdminInstitutionDetail; message: string };
type AdminInstitutionMemberResponse = { member: AdminInstitutionMember; message: string };

/**
 * Mirrors `apps/server/src/modules/admin-institution` (`adminInstitutionController`, prefix
 * `/admin/institutions`) — the admin panel's institution management (regular vs complimentary
 * seats, trial, subscription). Every mutation invalidates both the institution's own id and
 * `LIST`, since `AdminInstitutionListItem` (the list row shape) carries the same
 * seat/subscription/trial counters as the detail view — any of these mutations can change what
 * the list row shows, not just the detail page.
 *
 * No cross-invalidation with the owner-facing `institution.api.ts` / `institution-member.api.ts`
 * tags: those are fetched by a different actor (the institution owner) in what is normally a
 * completely separate session/store from the admin panel. Even under impersonation (Phase 3,
 * client-side sessionStorage state per the project plan) the two tag namespaces don't represent
 * the same RTK Query store subscription automatically — if impersonation needs the owner-facing
 * cache invalidated after an admin action taken just before impersonating, that has to be an
 * explicit `dispatch(institutionApi.util.invalidateTags(...))` at the app layer when
 * impersonation starts, not something encoded in these tags.
 */
export const adminInstitutionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listAdminInstitutions: builder.query<ListAdminInstitutionsResponse, ListAdminInstitutionsPayload>({
      query: (payload) => ({ path: ['admin', 'institutions'], method: 'get', query: payload }),
      providesTags: (result) =>
        result
          ? [
              ...result.institutions.map(({ id }) => ({ type: 'AdminInstitution' as const, id })),
              { type: 'AdminInstitution' as const, id: 'LIST' },
            ]
          : [{ type: 'AdminInstitution' as const, id: 'LIST' }],
    }),

    getAdminInstitutionById: builder.query<AdminInstitutionDetail, string>({
      query: (institutionId) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }],
        method: 'get',
      }),
      transformResponse: (response: AdminInstitutionResponse) => response.institution,
      providesTags: (_result, _error, institutionId) => [{ type: 'AdminInstitution', id: institutionId }],
    }),

    getAdminInstitutionByUserId: builder.query<AdminInstitutionDetail, string>({
      query: (userId) => ({
        path: ['admin', 'institutions', 'by-user', { param: 'user_id', value: userId }],
        method: 'get',
      }),
      transformResponse: (response: AdminInstitutionResponse) => response.institution,
      providesTags: (result) => (result ? [{ type: 'AdminInstitution', id: result.id }] : []),
    }),

    updateAdminInstitution: builder.mutation<
      AdminInstitutionDetail,
      { institutionId: string; payload: UpdateAdminInstitutionPayload }
    >({
      query: ({ institutionId, payload }) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: AdminInstitutionMutationResponse) => response.institution,
      invalidatesTags: (_result, _error, { institutionId }) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),

    activateAdminInstitutionTrial: builder.mutation<
      AdminInstitutionDetail,
      { institutionId: string; payload: ActivateInstitutionTrialPayload }
    >({
      query: ({ institutionId, payload }) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }, 'trial'],
        method: 'post',
        body: payload,
      }),
      transformResponse: (response: AdminInstitutionMutationResponse) => response.institution,
      invalidatesTags: (_result, _error, { institutionId }) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),

    updateAdminInstitutionTrial: builder.mutation<
      AdminInstitutionDetail,
      { institutionId: string; payload: UpdateInstitutionTrialPayload }
    >({
      query: ({ institutionId, payload }) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }, 'trial'],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: AdminInstitutionMutationResponse) => response.institution,
      invalidatesTags: (_result, _error, { institutionId }) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),

    endAdminInstitutionTrial: builder.mutation<AdminInstitutionDetail, string>({
      query: (institutionId) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }, 'trial', 'end'],
        method: 'post',
      }),
      transformResponse: (response: AdminInstitutionMutationResponse) => response.institution,
      invalidatesTags: (_result, _error, institutionId) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),

    offerAdminInstitutionSeat: builder.mutation<
      AdminInstitutionMember,
      { institutionId: string; payload: OfferInstitutionSeatPayload }
    >({
      query: ({ institutionId, payload }) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }, 'seats', 'offer'],
        method: 'post',
        body: payload,
      }),
      transformResponse: (response: AdminInstitutionMemberResponse) => response.member,
      invalidatesTags: (_result, _error, { institutionId }) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),

    addAdminInstitutionMember: builder.mutation<
      AdminInstitutionMember,
      { institutionId: string; payload: AddAdminInstitutionMemberPayload }
    >({
      query: ({ institutionId, payload }) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }, 'members'],
        method: 'post',
        body: payload,
      }),
      transformResponse: (response: AdminInstitutionMemberResponse) => response.member,
      invalidatesTags: (_result, _error, { institutionId }) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),

    removeAdminInstitutionMember: builder.mutation<void, { institutionId: string; userId: string }>({
      query: ({ institutionId, userId }) => ({
        path: [
          'admin',
          'institutions',
          { param: 'institution_id', value: institutionId },
          'members',
          { param: 'user_id', value: userId },
        ],
        method: 'delete',
      }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, { institutionId }) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),

    cancelAdminInstitutionSubscription: builder.mutation<void, string>({
      query: (institutionId) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }, 'cancel-subscription'],
        method: 'post',
      }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, institutionId) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),

    reactivateAdminInstitutionSubscription: builder.mutation<void, string>({
      query: (institutionId) => ({
        path: ['admin', 'institutions', { param: 'institution_id', value: institutionId }, 'reactivate-subscription'],
        method: 'post',
      }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, institutionId) => [
        { type: 'AdminInstitution', id: institutionId },
        { type: 'AdminInstitution', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListAdminInstitutionsQuery,
  useGetAdminInstitutionByIdQuery,
  useGetAdminInstitutionByUserIdQuery,
  useUpdateAdminInstitutionMutation,
  useActivateAdminInstitutionTrialMutation,
  useUpdateAdminInstitutionTrialMutation,
  useEndAdminInstitutionTrialMutation,
  useOfferAdminInstitutionSeatMutation,
  useAddAdminInstitutionMemberMutation,
  useRemoveAdminInstitutionMemberMutation,
  useCancelAdminInstitutionSubscriptionMutation,
  useReactivateAdminInstitutionSubscriptionMutation,
} = adminInstitutionApi;
